// src/pages/LandingPage.tsx
import { useEffect } from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Features from '../components/Features';
import ContactForm from '../components/ContactForm';
import Footer from '../components/Footer';
import { useAnalytics } from '../hooks/useAnalytics';

// Structured data for rich search results
const structuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "SignoBox",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Any",
  "description": "Email signature management tool for Google Workspace that integrates with HR systems",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingCount": "1",
    "bestRating": "5",
    "worstRating": "1",
    "ratingValue": "5"
  },
  "offers": {
    "@type": "Offer",
    "availability": "PreOrder",
    "price": "0",
    "priceCurrency": "USD"
  },
  "keywords": "Email signatures, Google Workspace, brand consistency, HR integration, centralized management",
  "softwareFeatures": [
    "HR system integration",
    "Centralized signature management",
    "Drag-and-drop template builder",
    "Seamless Gmail integration"
  ]
};

const LandingPage = () => {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    // Add structured data to the page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Track page view on mount
    trackEvent('page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname
    });

    // Track scroll depth
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100
      );
      
      // Track 50% and 90% scroll depth
      if (scrollPercent >= 50 && !window.hasReached50Percent) {
        window.hasReached50Percent = true;
        trackEvent('scroll_depth', {
          percent: 50,
          page_path: window.location.pathname
        });
      }
      
      if (scrollPercent >= 90 && !window.hasReached90Percent) {
        window.hasReached90Percent = true;
        trackEvent('scroll_depth', {
          percent: 90,
          page_path: window.location.pathname
        });
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Track time spent
    const startTime = Date.now();
    const timeTracker = setInterval(() => {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      if (timeSpent === 30) {
        trackEvent('time_spent', { seconds: 30 });
      } else if (timeSpent === 60) {
        trackEvent('time_spent', { seconds: 60 });
        clearInterval(timeTracker);
      }
    }, 1000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(timeTracker);
      // Clean up structured data
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [trackEvent]);

  // Track copy to clipboard events
  useEffect(() => {
    const handleCopy = () => {
      trackEvent('content_copy', {
        page_path: window.location.pathname
      });
    };

    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, [trackEvent]);

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <main id="main-content">
        <Hero />
        <Features />
        <ContactForm />
      </main>
      <Footer />
    </div>
  );
};

// Update global window interface
declare global {
  interface Window {
    hasReached50Percent?: boolean;
    hasReached90Percent?: boolean;
  }
}

export default LandingPage;
